import { ComponentProps } from "react";
import { Avatar, Box, Stack, Typography, useTheme } from "@mui/material";
import { FoodsIconButton, FoodsTextInput, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ComposerPreset } from "./CreatePostForm";
import { SocialPublishActions } from "../Prompts/SocialPublishActions";

interface CompactCreatePostProps {
    user: any;
    onExpand: (preset?: ComposerPreset) => void;
}

export const CompactCreatePost = ({ user, onExpand }: CompactCreatePostProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const mediaActions: Array<{
        label: string;
        icon: ComponentProps<typeof FoodsIconButton>["icon"];
        preset: ComposerPreset;
    }> = [
        {
            label: t("social.create_post.toolbar.photo", "Photo"),
            icon: "gallery",
            preset: { openImageUpload: true },
        },
        {
            label: t("social.create_post.toolbar.document", "Document"),
            icon: "documentSpace",
            preset: { openDocumentUpload: true },
        },
    ];

    return (
        <WhiteBox
            sx={{
                mb: 3,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                backgroundColor: theme.palette.primary[50],
                border: `1px solid ${theme.palette.primary[200]}`,
                "&:hover": {
                    boxShadow: theme.custom.shadows.grey,
                    transform: "translateY(-1px)",
                },
            }}
        >
            <Stack gap={2} sx={{ p: 2 }}>
                <Stack
                    direction="row"
                    gap={2}
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() => onExpand()}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onExpand();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <Avatar
                        variant="medium"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            boxShadow: `0 0 0 3px ${theme.palette.primary[100]}`,
                        }}
                    >
                        {user?.firstname?.[0]}
                        {user?.lastname?.[0]}
                    </Avatar>

                    <FoodsTextInput
                        value=""
                        onChange={() => undefined}
                        ExtraInputProps={{
                            readOnly: true,
                            placeholder: t(
                                "social.create_post.compact_placeholder",
                                "Share a question, a best practice, or feedback with your peers…",
                            ),
                            onClick: () => onExpand(),
                        }}
                        StackProps={{ width: "100%" }}
                    />
                </Stack>

                <SocialPublishActions onActionClick={onExpand} />

                <Stack direction="row" gap={1} flexWrap="wrap">
                    {mediaActions.map((action) => (
                        <Box
                            key={action.label}
                            role="button"
                            tabIndex={0}
                            onClick={() => onExpand(action.preset)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onExpand(action.preset);
                                }
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: theme.palette.common.white,
                                borderRadius: 999,
                                border: `1px solid ${theme.custom.grey[400]}`,
                                color: theme.custom.grey[2800],
                                cursor: "pointer",
                                px: 1.5,
                                py: 0.75,
                                transition: "background-color 0.15s ease",
                                "&:hover": {
                                    backgroundColor: theme.custom.grey[100],
                                },
                            }}
                        >
                            <FoodsIconButton size="xs" icon={action.icon} withBackground />
                            <Typography variant="small-bold">{action.label}</Typography>
                        </Box>
                    ))}
                </Stack>
            </Stack>
        </WhiteBox>
    );
};
